// Copyright (C) 2022 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import Alert from 'antd/lib/alert';
import Progress from 'antd/lib/progress';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Text from 'antd/lib/typography/Text';
import List from 'antd/lib/list';

interface Props {
    tasks: any[];
    onCancel: () => void;
    onOk: () => void;
    onRetryFailedTasks: () => void;
    onRetryCancelledTasks: () => void;
}

export default function MultiTasksProgress(props: Props): JSX.Element {
    const {
        tasks: items,
        onOk,
        onCancel,
        onRetryFailedTasks,
        onRetryCancelledTasks,
    } = props;
    let alertType: any = 'info';

    const countPending = items.filter((item) => item.status === 'pending').length;
    const countProgress = items.filter((item) => item.status === 'progress').length;
    const countCompleted = items.filter((item) => item.status === 'completed').length;
    const countFailed = items.filter((item) => item.status === 'failed').length;
    const countCancelled = items.filter((item) => item.status === 'cancelled').length;
    const countAll = items.length;
    const countCreated = items.filter((item) => item.status === 'completed').length;
    const countTranscriptionQueued = items.filter((item) => item.transcriptionState === 'Queued').length;
    const countTranscriptionStarted = items.filter((item) => item.transcriptionState === 'Started').length;
    const countTranscriptionFinished = items.filter((item) => item.transcriptionState === 'Finished').length;
    const countTranscriptionFailed = items.filter((item) => item.transcriptionState === 'Failed').length;
    const countTranscriptionPending = items.filter(
        (item) => item.status === 'completed' && !item.transcriptionState,
    ).length;
    const countTranscriptionQueuedTotal = countTranscriptionQueued + countTranscriptionPending;
    const hasTranscriptionStatuses =
        countTranscriptionQueued +
        countTranscriptionStarted +
        countTranscriptionFinished +
        countTranscriptionFailed > 0;
    const shouldShowTranscriptionSummary = countCreated > 0 || hasTranscriptionStatuses;
    const transcriptionDenominator = countCreated || (
        countTranscriptionQueuedTotal +
        countTranscriptionStarted +
        countTranscriptionFinished +
        countTranscriptionFailed
    );
    const transcriptionProgressUnits = items.reduce((acc: number, item: any) => {
        if (item.status !== 'completed') {
            return acc;
        }

        if (item.transcriptionState === 'Finished' || item.transcriptionState === 'Failed') {
            return acc + 1;
        }

        if (item.transcriptionState === 'Started') {
            const reportedProgress = Number.isFinite(item.transcriptionProgress) ?
                item.transcriptionProgress / 100 :
                0;
            return acc + Math.max(reportedProgress, 0.2);
        }

        return acc;
    }, 0);
    const transcriptionPercent = transcriptionDenominator ?
        Math.min(100, Math.round((transcriptionProgressUnits / transcriptionDenominator) * 100)) :
        0;
    let transcriptionProgressStatus: 'active' | 'success' | 'exception' = 'active';
    if (countTranscriptionFailed) {
        transcriptionProgressStatus = 'exception';
    } else if (transcriptionPercent === 100 && transcriptionDenominator > 0) {
        transcriptionProgressStatus = 'success';
    }
    const percent = countAll ?
        Math.ceil(((countAll - (countPending + countProgress)) / countAll) * 100) :
        0;
    let uploadProgressStatus: 'active' | 'success' | 'exception' = 'active';
    if (countFailed) {
        uploadProgressStatus = percent === 100 ? 'exception' : 'active';
    } else if (percent === 100) {
        uploadProgressStatus = 'success';
    }

    const failedFiles: string[] = percent === 100 && countFailed ?
        items.filter((item) => item.status === 'failed')
            .map((item): string => {
                const tabs = Object.keys(item.files);
                const itemType = tabs.find((key) => (item.files[key][0])) || 'local';
                return item.files[itemType][0]?.name || item.files[itemType][0] || '';
            })
            .filter(Boolean) :
        [];

    if (percent === 100) {
        if (countFailed === countAll) {
            alertType = 'error';
        } else if (countFailed) {
            alertType = 'warning';
        }
    }

    return (
        <Alert
            type={alertType}
            message={(
                <div className='cvat-create-multi-tasks-summary'>
                    {percent === 100 ? (
                        <Row className='cvat-create-multi-tasks-state' align='middle'>
                            <Col>
                                <Text strong>Finished</Text>
                            </Col>
                        </Row>
                    ) : null}
                    <div className='cvat-create-multi-tasks-section'>
                        <Text strong className='cvat-create-multi-tasks-section-title'>Task creation</Text>
                        <div className='cvat-create-multi-tasks-metrics'>
                            <Text className='cvat-create-multi-tasks-metric'>{`Pending: ${countPending}`}</Text>
                            <Text className='cvat-create-multi-tasks-metric'>{`Progress: ${countProgress}`}</Text>
                            <Text className='cvat-create-multi-tasks-metric'>{`Completed: ${countCompleted}`}</Text>
                            <Text className='cvat-create-multi-tasks-metric cvat-create-multi-tasks-metric-failed'>
                                {`Failed: ${countFailed}`}
                            </Text>
                            {countCancelled ? (
                                <Text className='cvat-create-multi-tasks-metric'>{`Cancelled: ${countCancelled}`}</Text>
                            ) : null}
                            <Text className='cvat-create-multi-tasks-metric'>{`Total: ${countAll}`}</Text>
                        </div>
                        <Progress
                            status={uploadProgressStatus as 'active' | 'success' | 'exception'}
                            percent={percent}
                            strokeWidth={5}
                            size='small'
                            trailColor='#d8d8d8'
                        />
                    </div>
                    {shouldShowTranscriptionSummary ? (
                        <div className='cvat-create-multi-tasks-section'>
                            <Text strong className='cvat-create-multi-tasks-section-title'>Transcription</Text>
                            <div className='cvat-create-multi-tasks-metrics'>
                                <Text className='cvat-create-multi-tasks-metric'>
                                    {`Queued: ${countTranscriptionQueuedTotal}`}
                                </Text>
                                <Text className='cvat-create-multi-tasks-metric'>
                                    {`In progress: ${countTranscriptionStarted}`}
                                </Text>
                                <Text className='cvat-create-multi-tasks-metric'>
                                    {`Completed: ${countTranscriptionFinished}`}
                                </Text>
                                <Text className='cvat-create-multi-tasks-metric cvat-create-multi-tasks-metric-failed'>
                                    {`Failed: ${countTranscriptionFailed}`}
                                </Text>
                            </div>
                            <Text type='secondary' className='cvat-create-multi-tasks-progress-label'>
                                {`Transcription progress: ${transcriptionPercent}%`}
                            </Text>
                            <Progress
                                status={transcriptionProgressStatus as 'active' | 'success' | 'exception'}
                                percent={transcriptionPercent}
                                strokeWidth={5}
                                size='small'
                                trailColor='#d8d8d8'
                                showInfo={false}
                            />
                        </div>
                    ) : null}
                    {percent === 100 && countFailed ? (
                        <Row className='cvat-create-multi-tasks-failed-files'>
                            <Collapse style={{
                                width: '100%',
                                marginBottom: 5,
                            }}
                            >
                                <Collapse.Panel
                                    header={(
                                        <Text strong>
                                            Failed files
                                        </Text>
                                    )}
                                    key='appearance'
                                >
                                    <List
                                        size='small'
                                        dataSource={failedFiles}
                                        renderItem={(item: string) => <List.Item>{ item }</List.Item>}
                                    />
                                </Collapse.Panel>
                            </Collapse>
                        </Row>
                    ) : null }
                    <Row justify='end' gutter={5}>
                        {percent === 100 ?
                            (
                                <>
                                    <Col>
                                        <Button
                                            className='cvat-create-multiple-tasks-retry-failed-button'
                                            disabled={!countFailed}
                                            onClick={onRetryFailedTasks}
                                        >
                                            Retry failed tasks
                                        </Button>
                                    </Col>
                                    {
                                        countCancelled ? (
                                            <Col>
                                                <Button
                                                    className='cvat-create-multiple-tasks-retry-cancelled-button'
                                                    disabled={!countCancelled}
                                                    onClick={onRetryCancelledTasks}
                                                >
                                                    Retry cancelled tasks
                                                </Button>
                                            </Col>
                                        ) : null
                                    }
                                    <Col>
                                        <Button
                                            className='cvat-create-multiple-tasks-after-finish-button'
                                            type='primary'
                                            onClick={onOk}
                                        >
                                            Ok
                                        </Button>
                                    </Col>
                                </>
                            ) : (
                                <Col>
                                    <Button
                                        className='cvat-create-multiple-tasks-cancel-pending-button'
                                        onClick={onCancel}
                                        disabled={!countPending}
                                    >
                                        Cancel pending tasks
                                    </Button>
                                </Col>
                            )}
                    </Row>
                </div>
            )}
        />
    );
}
